import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import logo from '../assets/logo-horizontal.png';
import { track, PRODUCT_DROPDOWN } from '../utils/analytics';

const PRODUCT_LINKS = [
  {
    to: '/products/post-match',
    label: 'Tactmark Post-Match',
    description: 'Turn match evidence into coaching decisions, training priorities, and trackable improvement.',
  },
  {
    to: '/products/federation',
    label: 'Tactmark Federation',
    description: 'Technical-development oversight, standardized reporting, accountability, and organization-level evidence for football associations.',
  },
];

const NAV_LINKS = [
  { to: '/how-it-works', label: 'How it works' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/insights', label: 'Insights' },
  { to: '/about', label: 'About' },
];

export default function PublicNav() {
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const location = useLocation();
  const productsRef = useRef(null);
  const hoverOpenedRef = useRef(false);
  const productsActive = location.pathname.startsWith('/products');

  useEffect(() => {
    setProductsOpen(false);
    setOpen(false);
    setMobileProductsOpen(false);
    hoverOpenedRef.current = false;
  }, [location.pathname]);

  useEffect(() => {
    if (!productsOpen) return undefined;
    const onPointerDown = (event) => {
      if (productsRef.current && !productsRef.current.contains(event.target)) {
        setProductsOpen(false);
        hoverOpenedRef.current = false;
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setProductsOpen(false);
        hoverOpenedRef.current = false;
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [productsOpen]);

  const openProductsFromHover = () => {
    hoverOpenedRef.current = true;
    setProductsOpen(true);
  };

  const closeProductsFromHover = () => {
    hoverOpenedRef.current = false;
    setProductsOpen(false);
  };

  const toggleProductsFromClick = () => {
    // Hover may have already requested open before React committed state.
    // Keep the menu open instead of toggling it closed on the same pointer sequence.
    if (hoverOpenedRef.current) {
      hoverOpenedRef.current = false;
      setProductsOpen(true);
      return;
    }
    setProductsOpen((value) => !value);
  };

  const linkClass = (to) =>
    `text-white transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900 rounded-sm ${
      location.pathname.startsWith(to) ? 'font-semibold opacity-100' : 'font-medium opacity-80 hover:opacity-100'
    }`;

  return (
    <nav className="bg-navy-900 relative z-40" aria-label="Primary">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          <Link to="/" className="flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 rounded-sm" onClick={() => setOpen(false)}>
            <img src={logo} alt="Tactmark" className="h-11" />
          </Link>

          <div className="hidden md:flex items-center gap-8 text-sm">
            <div
              className="relative"
              ref={productsRef}
              onMouseEnter={openProductsFromHover}
              onMouseLeave={closeProductsFromHover}
            >
              <button
                type="button"
                onClick={toggleProductsFromClick}
                aria-expanded={productsOpen}
                aria-haspopup="menu"
                aria-controls="products-menu"
                className={`inline-flex items-center gap-1 text-white transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-navy-900 rounded-sm ${
                  productsActive ? 'font-semibold opacity-100' : 'font-medium opacity-80 hover:opacity-100'
                }`}
              >
                Products
                <ChevronDown className={`w-4 h-4 transition-transform ${productsOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
              {productsOpen && (
                <div
                  id="products-menu"
                  role="menu"
                  className="absolute left-0 top-full pt-2"
                >
                  <div className="w-[22rem] max-w-[calc(100vw-2rem)] bg-white border border-gray-200 rounded shadow-lg py-2">
                    {PRODUCT_LINKS.map((product) => (
                      <Link
                        key={product.to}
                        to={product.to}
                        role="menuitem"
                        onClick={() => {
                          track(PRODUCT_DROPDOWN, { destination: product.to });
                          setProductsOpen(false);
                        }}
                        className="block px-4 py-3 hover:bg-navy-50 focus:bg-navy-50 focus:outline-none"
                      >
                        <span className="block text-sm font-semibold text-navy-900">{product.label}</span>
                        <span className="block text-xs text-gray-500 mt-1 leading-relaxed">{product.description}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {NAV_LINKS.map(({ to, label }) => (
              <Link key={to} to={to} className={linkClass(to)}>
                {label}
              </Link>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/login"
              className="border border-navy-500 text-white text-sm font-medium px-4 py-1.5 rounded hover:bg-white hover:text-navy-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              Sign In
            </Link>
            <Link
              to="/login?mode=signup"
              className="bg-white text-navy-900 text-sm font-medium px-4 py-1.5 rounded hover:bg-navy-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              Start free trial
            </Link>
          </div>

          <div className="md:hidden flex items-center gap-3">
            <Link
              to="/login?mode=signup"
              className="bg-white text-navy-900 text-xs font-semibold px-3 py-1.5 rounded"
            >
              Start free trial
            </Link>
            <button
              type="button"
              className="text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 rounded-sm"
              onClick={() => setOpen(!open)}
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-navy-900 border-t border-navy-700 px-6 py-4 space-y-3">
          <button
            type="button"
            onClick={() => setMobileProductsOpen((value) => !value)}
            aria-expanded={mobileProductsOpen}
            className="w-full flex items-center justify-between text-sm text-white font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 rounded-sm"
          >
            Products
            <ChevronDown className={`w-4 h-4 transition-transform ${mobileProductsOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
          </button>
          {mobileProductsOpen && (
            <div className="pl-3 space-y-3 border-l border-navy-700">
              {PRODUCT_LINKS.map((product) => (
                <Link
                  key={product.to}
                  to={product.to}
                  onClick={() => {
                    track(PRODUCT_DROPDOWN, { destination: product.to });
                    setOpen(false);
                  }}
                  className={`block text-sm text-white ${location.pathname.startsWith(product.to) ? 'font-semibold' : 'font-medium opacity-80'}`}
                >
                  {product.label}
                </Link>
              ))}
            </div>
          )}
          {NAV_LINKS.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              onClick={() => setOpen(false)}
              className={`block text-sm text-white ${location.pathname.startsWith(to) ? 'font-semibold' : 'font-medium opacity-80'}`}
            >
              {label}
            </Link>
          ))}
          <Link
            to="/login"
            onClick={() => setOpen(false)}
            className="block border border-navy-500 text-white text-sm font-medium px-4 py-2 rounded text-center hover:bg-white hover:text-navy-900 transition-colors mt-2"
          >
            Sign In
          </Link>
        </div>
      )}
    </nav>
  );
}
